import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import ImageSet from '~/components/Gallery/ImageSet';
import Loading from '~/components/Loading';
import Pagination from '~/components/Pagination';

import { useAuth } from '~/modules/auth/useAuth';
import { getVisibilitySettings } from '~/modules/contentVisibility';
import { useUserGallery } from '~/modules/gallery/queries';

import { useAppSelector } from '~/hooks/redux';

export default function UserGallery({
  user,
  showFavorites = false,
}: {
  user: string;
  showFavorites?: boolean;
}) {
  const [page, setPage] = useState(1);
  const { currentUser } = useAuth();
  const adultToggleState = useAppSelector(getVisibilitySettings);
  const queryClient = useQueryClient();

  const { data, isLoading } = useUserGallery(user, page, showFavorites);

  const reloadData = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['user-gallery', user, page, showFavorites],
      refetchType: 'all',
    });
  }, [user, page, showFavorites, queryClient]);

  useEffect(() => {
    setPage(1);
  }, [user, showFavorites]);

  const needsPagination = useMemo(() => {
    return data && data?.meta.total_pages > 1;
  }, [data]);

  if (isLoading || !data) {
    return <Loading />;
  }

  return (
    <div>
      {needsPagination && (
        <Pagination
          page={page}
          totalPages={data?.meta.total_pages}
          onChange={setPage}
        />
      )}

      {isLoading ? (
        <Loading />
      ) : (
        <ImageSet
          onDelete={reloadData}
          currentUser={currentUser}
          images={data.data}
          adultToggleState={adultToggleState}
          noUsernames
        />
      )}

      {needsPagination && (
        <Pagination
          page={page}
          totalPages={data?.meta.total_pages}
          onChange={setPage}
        />
      )}
    </div>
  );
}
